import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import * as Sentry from '@sentry/react';
import { actions } from '@store';
import type { QueryClient } from '@tanstack/react-query';
import { markExpectedAuthLogout } from '@utils/sentry/apiError';

import type { Store } from '../configureStore.js';
import { authInitialState, type AuthState } from '../features/authSlice.js';

import { decodeToken } from './decodeToken.js';

declare module 'axios' {
  // Marks a request that has already been retried after a 401 refresh, so the
  // response interceptor never retries it a second time.
  interface InternalAxiosRequestConfig {
    _retried?: boolean;
  }
}

// SimpleJWT access tokens are short-lived (5 min). Refresh is purely reactive: it
// only runs when a request actually 401s (see `attachAuthRetryInterceptor`), never
// on a background timer — a timer running in every open tab regardless of focus
// was what caused concurrent tabs to race to rotate the same refresh token in the
// first place (SPAZ2-3301). This module is the single source of truth for
// performing the refresh: it is used by the reactive 401 response interceptor
// attached to the app's one axios instance (AppApiClient), and it de-duplicates
// concurrent callers so a burst of 401s triggers exactly one `auth/refresh/`
// round-trip.
//
// Multi-tab: refresh rotation blacklists the previous refresh token, but each tab
// has its own in-memory store, so a rotation in one tab would strand the pair the
// other tabs still hold. Three mechanisms keep concurrent tabs alive:
// 1. The persisted storage (`persist:root`) is the cross-tab source of truth: the
//    refresh token is re-read from it at call time, and a rotated pair is written
//    through to it synchronously (redux-persist's own flush is async, which would
//    leave a window where another tab still reads the blacklisted token).
// 2. A Web Locks API lock makes the refresh single-flight *across* tabs; after
//    acquiring it, storage is re-read — if another tab already rotated while we
//    waited, its pair is adopted and the network refresh is skipped.
// 3. Tabs adopt rotations eagerly via the `storage` event, and a rejected refresh
//    re-checks storage before logging out (recovery when Web Locks is unavailable
//    or a race slips through anyway).

const REFRESH_PATH = '/api/v3/backoffice/users/auth/refresh/';

// Cross-tab mutex name for the Web Locks API.
const REFRESH_LOCK_NAME = 'spazious-backoffice-auth-refresh';

// localStorage key redux-persist writes the root state under (`persist:` prefix +
// the `key: 'root'` of the persist config in configureStore).
const PERSIST_ROOT_STORAGE_KEY = 'persist:root';

// A stored pair only replaces a network refresh when its access token still has
// meaningfully more than an immediate-401 margin of validity left; a pair rotated
// by another tab has a full TTL (~5 min) and comfortably clears this bar, while a
// nearly-expired stored pair is rejected so we don't adopt a token that would just
// trigger another reactive refresh on the very next request.
const ADOPTABLE_ACCESS_MIN_VALIDITY_SECONDS = 60;

interface RefreshDeps {
  store: Store;
  // Origin of the backend API (e.g. `https://api.dev.spazious.com`).
  apiOrigin: string;
}

interface RefreshTokenPair {
  access: string;
  refresh: string;
}

let deps: RefreshDeps | null = null;
// Registered separately from `deps` because the React Query cache only exists
// inside `BackofficeProvider`, which is a descendant of the component that knows
// the store + API origin. On refresh failure we clear it to drop stale data.
let queryClient: QueryClient | null = null;

// Module-level in-flight promise: concurrent callers await the same refresh.
let pending: Promise<string> | null = null;

let crossTabListenerAttached = false;

const isRefreshTokenPair = (value: unknown): value is RefreshTokenPair => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.access === 'string' && typeof candidate.refresh === 'string';
};

const getAuth = (): AuthState | null => deps?.store.getState().auth ?? null;

/**
 * Extract the `{ access, refresh }` pair from a raw redux-persist root payload
 * (each slice is stored double-serialized: a JSON string inside the root JSON).
 * Pure — takes the raw string so it has no DOM dependency.
 * Returns `null` unless both tokens are present.
 */
const parsePersistedTokenPair = (persistedRoot: string | null): RefreshTokenPair | null => {
  if (!persistedRoot) {
    return null;
  }
  try {
    const root: unknown = JSON.parse(persistedRoot);
    if (typeof root !== 'object' || root === null) {
      return null;
    }
    const rawAuth = (root as Record<string, unknown>).auth;
    if (typeof rawAuth !== 'string') {
      return null;
    }
    const auth: unknown = JSON.parse(rawAuth);
    if (typeof auth !== 'object' || auth === null) {
      return null;
    }
    const { token, refresh } = auth as Record<string, unknown>;
    if (
      typeof token !== 'string' ||
      token === '' ||
      typeof refresh !== 'string' ||
      refresh === ''
    ) {
      return null;
    }
    return { access: token, refresh };
  } catch {
    return null;
  }
};

/**
 * Serialize `auth` into a raw redux-persist root payload, preserving the other
 * slices. Pure counterpart of `parsePersistedTokenPair`; returns `null` when the
 * payload is absent or malformed (nothing to merge into).
 */
const mergeAuthIntoPersistedRoot = (
  persistedRoot: string | null,
  auth: AuthState
): string | null => {
  if (!persistedRoot) {
    return null;
  }
  try {
    const root: unknown = JSON.parse(persistedRoot);
    if (typeof root !== 'object' || root === null) {
      return null;
    }
    return JSON.stringify({ ...(root as Record<string, unknown>), auth: JSON.stringify(auth) });
  } catch {
    return null;
  }
};

const readStoredTokenPair = (): RefreshTokenPair | null => {
  try {
    return parsePersistedTokenPair(window.localStorage.getItem(PERSIST_ROOT_STORAGE_KEY));
  } catch {
    return null;
  }
};

// Write the auth slice through to localStorage synchronously. redux-persist's
// persistoid flushes on a timer (async), so without this a tab reading storage
// right after a rotation would still see the previous — now blacklisted — refresh
// token. redux-persist later rewrites the same values from the store, so the two
// writers stay consistent.
const persistAuthToStorage = (auth: AuthState): void => {
  try {
    const current = window.localStorage.getItem(PERSIST_ROOT_STORAGE_KEY);
    const next = mergeAuthIntoPersistedRoot(current, auth);
    if (next !== null && next !== current) {
      window.localStorage.setItem(PERSIST_ROOT_STORAGE_KEY, next);
    }
  } catch {
    // Storage unavailable — redux-persist's own async flush remains the fallback.
  }
};

// Update the in-memory store with a pair rotated elsewhere (another tab), keeping
// every other auth field (scopes, role, impersonation state, grid config) as-is.
// No storage write: the pair came *from* storage.
// A store without a token means the session was signed out (or never existed):
// never adopt credentials into it — a late adoption racing an explicit logout
// would resurrect the session the user just ended, and adopting another tab's
// login into an empty store would build a half-session with no scopes.
const adoptTokenPair = (pair: RefreshTokenPair): void => {
  const auth = getAuth();
  if (!deps || !auth?.token) {
    return;
  }
  if (auth.token === pair.access && auth.refresh === pair.refresh) {
    return;
  }
  deps.store.dispatch(actions.setAuth({ ...auth, token: pair.access, refresh: pair.refresh }));
};

// Commit a pair we rotated ourselves: in-memory store + synchronous storage
// write-through so other tabs observe it immediately.
// Same signed-out guard as `adoptTokenPair`: a rotation that settles after a
// logout reset must not write credentials back into the emptied store/storage.
// The caller still receives the rotated access for its in-flight retry (e.g. the
// logout request itself), which is exactly what that retry needs to be delivered.
const commitRotatedPair = (pair: RefreshTokenPair): void => {
  const auth = getAuth();
  if (!deps || !auth?.token) {
    return;
  }
  const next: AuthState = { ...auth, token: pair.access, refresh: pair.refresh };
  deps.store.dispatch(actions.setAuth(next));
  persistAuthToStorage(next);
};

// `storage` events only fire in *other* tabs, so adopting here cannot loop back
// into the tab that wrote the rotation. `adoptTokenPair` is a no-op when the pair
// already matches, which also breaks any write ping-pong between tabs.
const handleStorageEvent = (event: StorageEvent): void => {
  if (event.key !== PERSIST_ROOT_STORAGE_KEY) {
    return;
  }
  const stored = parsePersistedTokenPair(event.newValue);
  if (stored) {
    adoptTokenPair(stored);
  }
};

/**
 * Register the store + API origin the refresh helper needs. Called once at app
 * init (AppApiClient). Also starts listening for token rotations persisted by
 * other tabs (idempotent across calls).
 */
export const configureAuthRefresh = (next: RefreshDeps): void => {
  deps = next;
  if (!crossTabListenerAttached && typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorageEvent);
    crossTabListenerAttached = true;
  }
};

/**
 * Register the React Query cache (from inside `BackofficeProvider`) so a failed
 * refresh can clear it on logout.
 */
export const registerRefreshQueryClient = (next: QueryClient): void => {
  queryClient = next;
};

/**
 * Whether `token` is within `thresholdSeconds` of expiry (or already expired /
 * undecodable). Used by `performRefresh` to decide whether a pair adopted from
 * storage (rotated by another tab) is fresh enough to skip a network refresh.
 */
const isAccessTokenNearExpiry = (token: string, thresholdSeconds: number): boolean => {
  let exp: unknown;
  try {
    ({ exp } = decodeToken(token));
  } catch {
    // Treat an undecodable/garbage token as near expiry so the caller takes the
    // refresh path instead of adopting the corrupt token: in `performRefresh` a
    // `false` here would let a garbage stored token satisfy the adoptable-freshness
    // check and be replayed forever with no recovery.
    return true;
  }

  if (typeof exp !== 'number') {
    // No usable `exp` claim — treat as expired for the same self-heal reason.
    return true;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp - nowSeconds <= thresholdSeconds;
};

// Devices whose clock runs behind the server are the ones that hit the reactive
// 401 path hardest, so knowing the skew of affected devices is key to diagnosing
// "constant 401" reports. `iat` on a freshly-minted token is stamped with the
// server clock, so `iat - now` measures the device's skew directly (positive =
// device clock behind the server). Reported at most once per session.
const CLOCK_SKEW_REPORT_THRESHOLD_SECONDS = 60;
let clockSkewReported = false;

const reportClockSkewOnce = (freshAccessToken: string): void => {
  if (clockSkewReported) {
    return;
  }

  let iat: unknown;
  try {
    ({ iat } = decodeToken(freshAccessToken));
  } catch {
    return;
  }

  if (typeof iat !== 'number') {
    return;
  }

  const skewSeconds = iat - Math.floor(Date.now() / 1000);
  if (Math.abs(skewSeconds) < CLOCK_SKEW_REPORT_THRESHOLD_SECONDS) {
    return;
  }

  clockSkewReported = true;
  Sentry.captureMessage('Device clock skew detected against server-minted token', {
    level: 'warning',
    extra: { skewSeconds },
  });
};

// Run `task` while holding the cross-tab refresh lock, so at most one tab talks to
// `auth/refresh/` at a time. Falls back to plain execution when the Web Locks API
// is unavailable (old browser / insecure context) — the per-tab single-flight and
// the blacklisted-refresh recovery in `performRefresh` still cover that case.
const runExclusiveAcrossTabs = <T>(task: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) {
    return task();
  }
  return new Promise<T>((resolve, reject) => {
    locks
      .request(REFRESH_LOCK_NAME, async () => {
        try {
          resolve(await task());
        } catch (error) {
          reject(error);
        }
      })
      .catch(reject);
  });
};

const performRefresh = async (): Promise<string> => {
  if (!deps) {
    throw new Error('Auth refresh is not configured');
  }
  const { store, apiOrigin } = deps;

  // We hold the cross-tab lock: re-read the persisted pair first. If another tab
  // rotated while we waited (its access token is still comfortably fresh), adopt
  // its pair and skip the network refresh — our in-memory refresh token is already
  // blacklisted, and burning the stored one would be a pointless extra rotation.
  // Adoption only applies when the stored pair differs from the one this tab holds:
  // the request that brought us here carried the in-memory token, so replaying an
  // identical stored token is guaranteed to 401 again. The freshness check alone
  // cannot catch that — it trusts the device clock, and a clock running behind the
  // server makes a server-expired token look valid, which turned this fast-path
  // into an endless 401 loop that never reached `auth/refresh/`.
  const stored = readStoredTokenPair();
  if (
    stored &&
    stored.access !== getAuth()?.token &&
    !isAccessTokenNearExpiry(stored.access, ADOPTABLE_ACCESS_MIN_VALIDITY_SECONDS)
  ) {
    adoptTokenPair(stored);
    return stored.access;
  }

  // Storage is the cross-tab source of truth for the refresh token; fall back to
  // the in-memory value only when nothing usable is persisted.
  const refresh = stored?.refresh ?? getAuth()?.refresh;
  if (!refresh) {
    // Fail closed exactly like the rejected-refresh catch below: with no usable
    // refresh token the session is unrecoverable, so clear the query cache and
    // reset the auth store *before* throwing. Otherwise a truthy auth object
    // holding an unusable token keeps the route guard "authorized" and every
    // request loops on 401 (SPAZ2-3203). This path runs before the network call,
    // so it and the catch branch are mutually exclusive — no double reset.
    queryClient?.clear();
    store.dispatch(actions.setAuth(authInitialState));
    throw new Error('No refresh token available');
  }

  try {
    const response = await axios.post<unknown>(
      `${apiOrigin}${REFRESH_PATH}`,
      { refresh },
      { headers: { 'Content-Type': 'application/json' } }
    );

    if (!isRefreshTokenPair(response.data)) {
      throw new Error('Malformed refresh response');
    }

    const { access, refresh: rotatedRefresh } = response.data;
    reportClockSkewOnce(access);
    // Rotation is enabled, so persist the new refresh token alongside the access.
    commitRotatedPair({ access, refresh: rotatedRefresh });

    return access;
  } catch (error) {
    // Blacklisted-refresh recovery: a rejected refresh does not necessarily mean
    // the session is dead. If storage now holds a *different* refresh token,
    // another tab rotated (blacklisting the one we just sent) in a window the lock
    // did not cover — adopt its pair and resolve the queued callers with the fresh
    // access token instead of logging the session out.
    const latest = readStoredTokenPair();
    if (latest && latest.refresh !== refresh) {
      adoptTokenPair(latest);
      return latest.access;
    }

    // This ends the session: capture why the refresh was rejected before logging
    // out, so "users being kicked / seeing errors" reports are diagnosable from
    // Sentry instead of unreproducible.
    Sentry.captureMessage('Session refresh failed — logging out', {
      level: 'warning',
      extra: {
        status: axios.isAxiosError(error) ? error.response?.status : undefined,
        isNetworkError: axios.isAxiosError(error) && !error.response,
      },
    });

    queryClient?.clear();
    store.dispatch(actions.setAuth(authInitialState));
    throw error;
  }
};

/**
 * Perform a single-flight SimpleJWT refresh (per tab *and* across tabs).
 *
 * - Reads the refresh token from the persisted storage at call time (cross-tab
 *   source of truth), falling back to the redux store.
 * - Serializes with other tabs via the Web Locks API; a rotation another tab
 *   completed while we waited is adopted without a network round-trip.
 * - POSTs the flat `{ refresh }` body to `auth/refresh/` with a bare axios call
 *   (not the SDK client) so it can never recurse through the 401 interceptor.
 * - Persists the rotated `{ access, refresh }` pair — in-memory and written
 *   through to localStorage synchronously — preserving the other auth fields
 *   (scopes, role, userId, grid config, ...).
 * - On failure: first re-checks storage for a rotation by another tab (adopt, do
 *   not log out); only when the stored refresh is the one that failed does it
 *   clear the query cache, log the session out, and rethrow so the caller's
 *   request fails too.
 *
 * Returns the new access token.
 */
export const refreshAccessToken = (): Promise<string> => {
  if (pending) {
    return pending;
  }

  pending = runExclusiveAcrossTabs(performRefresh);

  // Clear the in-flight slot once it settles, regardless of outcome. The
  // trailing `.catch(() => {})` swallows the rejection on this derived chain
  // only: callers still see the real rejection through the `pending` promise
  // returned below. Without it, every ordinary session-expiry / blacklisted
  // refresh failure would surface as an `unhandledrejection` (and Sentry noise).
  pending
    .finally(() => {
      pending = null;
    })
    .catch(() => {});

  return pending;
};

// A 401 on a request that was replayed with a *freshly minted* access token means
// the backend refused a token it had just issued (deactivated user, revoked
// session) — worth knowing about, unlike an ordinary expiry. Reported at most once
// per session, for the same reason `reportClockSkewOnce` is: the condition is
// self-sustaining. Stock SimpleJWT refresh validates only the refresh JWT and never
// loads the user, so it keeps returning 200 while every replay keeps 401ing, and
// React Query retries each failing query — an unlatched capture would emit events
// for as long as the tab stays open.
let postRefreshUnauthorizedReported = false;

const reportPostRefreshUnauthorizedOnce = (error: AxiosError): void => {
  if (postRefreshUnauthorizedReported) {
    return;
  }

  postRefreshUnauthorizedReported = true;
  Sentry.captureException(error);
};

/**
 * Re-arm the once-per-session diagnostics (clock skew, post-refresh 401) for a new
 * authenticated session.
 *
 * Both latches are module state, and signing out is a pure SPA transition — no
 * reload — so a second login in the same tab would otherwise inherit a latch that
 * is already spent and silently lose the report for that session. Called from the
 * sign-out → sign-in boundary only: token rotation and impersonation happen inside
 * one session and must keep the latch, which is what bounds the capture.
 */
export const resetAuthSessionDiagnostics = (): void => {
  clockSkewReported = false;
  postRefreshUnauthorizedReported = false;
};

const shouldSkipRetry = (config: InternalAxiosRequestConfig | undefined, url: string): boolean => {
  // Never retry the auth handshake itself, and never retry a request twice.
  // `logout` is deliberately NOT skipped: the endpoint requires a live access token
  // (IsAuthenticated), so a logout issued right after the access expired would 401
  // silently and leave the refresh token un-blacklisted server-side. Letting it
  // refresh-and-retry cannot recurse (the refresh path itself is still skipped).
  return /auth\/(login|refresh)\//.test(url) || Boolean(config?._retried);
};

/**
 * Attach a 401 response interceptor that transparently refreshes the access token
 * and retries the original request once. Idempotent across instances because each
 * call only adds its interceptor to the instance passed in.
 *
 * Accepts `undefined` and verifies the interceptor surface at runtime: the SDK
 * client's instance is obtained by reaching into SDK internals (`getClientAxios`
 * in `AppApiClient`), so if the SDK ever changes shape we degrade to "no
 * transparent 401 retry" (requests fail loudly) instead of crashing at mount.
 */
export const attachAuthRetryInterceptor = (instance: AxiosInstance | undefined): void => {
  if (!instance || typeof instance.interceptors?.response?.use !== 'function') {
    return;
  }

  instance.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!axios.isAxiosError(error)) {
        return Promise.reject(error);
      }

      const { config, response } = error;
      const url = config?.url ?? '';

      if (response?.status !== 401 || shouldSkipRetry(config, url) || !config) {
        if (response?.status === 401 && config?._retried) {
          reportPostRefreshUnauthorizedOnce(error);
        }
        return Promise.reject(error);
      }

      let access: string;
      try {
        access = await refreshAccessToken();
      } catch {
        // The helper already logged the session out; propagate the original error.
        // It is the expected end of an expired session, not an incident: mark it so
        // Sentry drops it, keeping the explicit "Session refresh failed" warning as
        // the single, diagnosable report of the logout.
        markExpectedAuthLogout(error);
        return Promise.reject(error);
      }

      config._retried = true;
      // Overwrite any stale Authorization on the replayed request with the fresh
      // token (`true` forces the rewrite even if the header is already present).
      config.headers.set('Authorization', `JWT ${access}`, true);

      return instance.request(config);
    }
  );
};
